import { QueryKey, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import { HttpException } from '../exceptions/index';

const FALLBACK_ERROR_MESSAGE = 'Request failed';

type AppQueryProps<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = {
  defaultErrorHandling?: boolean;
  onError?: (error: TError) => void;
} & UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;

export const useAppQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>({
  onError,
  defaultErrorHandling = true,
  ...params
}: AppQueryProps<TQueryFnData, TError, TData, TQueryKey>) => {
  const response = useQuery({
    ...params,
  });

  const lastErrorRef = useRef<null | TError>(null);

  useEffect(() => {
    if (response.isError && response.error !== lastErrorRef.current) {
      lastErrorRef.current = response.error;

      if (!defaultErrorHandling || !(response.error instanceof HttpException)) {
        return onError?.(response.error);
      }

      onError?.(response.error);
      const errorMessage =
        response.error.body?.message || response.error.message || FALLBACK_ERROR_MESSAGE;
      toast(`${errorMessage}`, {
        position: 'bottom-center',
        type: 'error',
        theme: 'light',
      });
    }

    if (!response.isError) {
      lastErrorRef.current = null;
    }
  }, [response.isError, response.error, onError, defaultErrorHandling]);

  return response;
};
